import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle } from "vue"

const _hoisted_1 = { class: "conflict-overview" }
const _hoisted_2 = {
  key: 0,
  class: "alert alert-info"
}
const _hoisted_3 = {
  key: 0,
  class: "text-muted mb-3"
}
const _hoisted_4 = {
  key: 0,
  class: "alert alert-success"
}
const _hoisted_5 = { class: "d-flex flex-wrap align-items-center gap-2 mb-3" }
const _hoisted_6 = { class: "fw-semibold me-1" }
const _hoisted_7 = ["title", "onClick"]
const _hoisted_8 = {
  class: "btn-group btn-group-sm ms-auto",
  role: "group",
  "aria-label": "Gruppierung"
}
const _hoisted_9 = ["onClick"]
const _hoisted_10 = {
  key: 0,
  class: "alert alert-secondary"
}
const _hoisted_11 = { class: "text-muted small fw-semibold d-flex align-items-center mb-2" }
const _hoisted_12 = { class: "badge text-bg-light ms-2" }
const _hoisted_13 = { class: "d-flex flex-column gap-2" }
const _hoisted_14 = { class: "card-body py-2 px-3" }
const _hoisted_15 = { class: "text-muted small" }
const _hoisted_16 = { class: "list-unstyled tt-parties mt-2 mb-0" }
const _hoisted_17 = ["href"]
const _hoisted_18 = { key: 1 }
const _hoisted_19 = {
  key: 2,
  class: "text-muted"
}
const _hoisted_20 = {
  key: 0,
  class: "mt-2"
}
const _hoisted_21 = {
  key: 0,
  class: "text-muted small mb-1"
}
const _hoisted_22 = { class: "tt-cohort-table" }
const _hoisted_23 = ["rowspan"]
const _hoisted_24 = { class: "tt-cohort-spec" }
const _hoisted_25 = { class: "tt-cohort-sem" }
const _hoisted_26 = { class: "d-flex flex-wrap gap-3 mt-2" }
const _hoisted_27 = ["onClick"]
const _hoisted_28 = ["href"]

import { computed, ref } from "vue";
import {
	conflictPartnerLabel,
	formatConflictDate,
} from "../../conflictFormat";


export default {
  __name: 'ConflictOverview',
  props: {
	conflicts: { type: Array, default: () => [] },
	summary: { type: Object, default: () => ({ room: 0, lecturer: 0, cohort: 0, total: 0 }) },
	loading: { type: Boolean, default: false },
	appliedFilters: { type: Boolean, default: false },
},
  emits: ["jump-to-date"],
  setup(__props) {

const props = __props;



// Colours and icons match the calendar conflict markings (room ↦ red, lecturer ↦ orange,
// cohort ↦ purple) so the two tabs read consistently.
const levelMeta = {
	room: { label: "Raum", color: "#dc3545", icon: "fa-location-dot" },
	lecturer: { label: "Dozent*in", color: "#fd7e14", icon: "fa-chalkboard-user" },
	cohort: { label: "Kohorte", color: "#6f42c1", icon: "fa-users" },
};
const severity = { room: 3, lecturer: 2, cohort: 1 };
const levelOrder = ["room", "lecturer", "cohort"];

// Placeholder row when a cohort group has no Vertiefung/Fachsemester entries.
const emptyEntry = { specialization: "", semester: "" };

const groupOptions = [
	{ value: "level", label: "Typ" },
	{ value: "date", label: "Datum" },
	{ value: "resource", label: "Ressource" },
];
const groupBy = ref("level");

// Only offer chips for the levels that actually occur.
const shownLevels = computed(() => levelOrder.filter((level) => props.summary[level] > 0));

const activeLevels = ref(new Set(levelOrder));
function toggleLevel(level) {
	const next = new Set(activeLevels.value);
	if (next.has(level)) {
		next.delete(level);
	} else {
		next.add(level);
	}
	activeLevels.value = next;
}

// "HH:MM–HH:MM" → { start, end } in minutes (tolerates en-dash or hyphen).
function parseWindow(timeStr) {
	const parts = (timeStr || "").split(/[–-]/);
	if (parts.length < 2) {
		return null;
	}
	const toMin = (value) => {
		const [h, m] = value.trim().split(":").map(Number);
		return Number.isNaN(h) ? NaN : h * 60 + (m || 0);
	};
	const start = toMin(parts[0]);
	const end = toMin(parts[1]);
	return Number.isNaN(start) || Number.isNaN(end) ? null : { start, end };
}

function formatWindow(start, end) {
	const f = (mins) =>
		`${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;
	return `${f(start)}–${f(end)}`;
}

// The span a single pair occupies = union of its two sides' times.
function conflictWindow(conflict) {
	const windows = [parseWindow(conflict.a?.time), parseWindow(conflict.b?.time)].filter(Boolean);
	if (!windows.length) {
		return { start: 0, end: 0 };
	}
	return {
		start: Math.min(...windows.map((w) => w.start)),
		end: Math.max(...windows.map((w) => w.end)),
	};
}

// The contested resource that becomes the card headline, plus a stable grouping key.
function contested(conflict) {
	if (conflict.level === "room") {
		const roomId = conflict.a?.roomId ?? conflict.b?.roomId ?? "";
		return { key: `room|${roomId}|${conflict.date}`, label: conflict.a?.room || conflict.b?.room || "Raum" };
	}
	if (conflict.level === "lecturer") {
		// The backend only exposes the lecturer label, not an id, so we key on the label. Approximate
		// for courses with several lecturers, but trennscharf enough in practice.
		const label = conflict.a?.lecturer || conflict.b?.lecturer || "Dozent*in";
		return { key: `lecturer|${label}|${conflict.date}`, label };
	}
	const text = cohortGroupsText(conflict.cohorts);
	return {
		key: `cohort|${text}|${conflict.date}`,
		label: text || "Kohorte",
	};
}

// Flatten structured cohort groups ([{ prefix, entries: [{ specialization, semester }] }]) into a
// single text line — used for stable grouping keys and the "by resource" group label.
function cohortGroupsText(groups) {
	return (groups || [])
		.map((group) => {
			const entries = (group.entries || [])
				.map((entry) => [entry.specialization, entry.semester].filter(Boolean).join(" "))
				.filter(Boolean)
				.join(", ");
			return [group.prefix, entries].filter(Boolean).join(" - ");
		})
		.filter(Boolean)
		.join("; ");
}

// Merge the per-conflict cohort groups of a cluster into one deduplicated structure (room/lecturer
// cards collect cohorts from several courses; cohort cards share an identical set, so this is a no-op
// there).
function mergeCohortGroups(groupArrays) {
	const byPrefix = new Map();
	for (const groups of groupArrays) {
		for (const group of groups || []) {
			if (!byPrefix.has(group.prefix)) {
				byPrefix.set(group.prefix, new Map());
			}
			const entries = byPrefix.get(group.prefix);
			for (const entry of group.entries || []) {
				entries.set(`${entry.specialization}|${entry.semester}`, entry);
			}
		}
	}
	return [...byPrefix.entries()].map(([prefix, entries]) => ({
		prefix,
		entries: [...entries.values()],
	}));
}

function sideKey(side) {
	return side.kind === "booking" ? `b${side.bookingId}` : `a${side.appointmentId}`;
}

// One party row: name (linked for appointments) plus only the non-contested details.
function buildParty(side, level, window) {
	const isBooking = side.kind === "booking";
	const name = conflictPartnerLabel(side);

	const bits = [];
	if (side.time && side.time !== window) {
		bits.push(side.time);
	}
	if (level === "room") {
		if (side.lecturer) bits.push(side.lecturer);
	} else if (level === "lecturer") {
		if (side.room) bits.push(side.room);
	} else {
		if (side.room) bits.push(side.room);
		if (side.lecturer) bits.push(side.lecturer);
	}

	return {
		key: sideKey(side),
		name,
		url: isBooking ? null : side.url,
		detail: bits.join(" · "),
	};
}

// Collapse the backend's symmetric pairs into "clash cards": one card per contested resource and
// overlapping time window, listing every involved party once (no A↔B / A↔C / B↔C explosion).
const cards = computed(() => {
	const byKey = new Map();
	for (const conflict of props.conflicts) {
		const key = contested(conflict).key;
		if (!byKey.has(key)) {
			byKey.set(key, []);
		}
		byKey.get(key).push(conflict);
	}

	const result = [];
	for (const list of byKey.values()) {
		const items = list
			.map((conflict) => ({ conflict, win: conflictWindow(conflict) }))
			.sort((a, b) => a.win.start - b.win.start || a.win.end - b.win.end);

		// Merge overlapping windows within the same contested resource into one cluster.
		let cluster = null;
		const flush = () => {
			if (cluster) result.push(finalizeCard(cluster));
		};
		for (const item of items) {
			if (cluster && item.win.start < cluster.end) {
				cluster.start = Math.min(cluster.start, item.win.start);
				cluster.end = Math.max(cluster.end, item.win.end);
				cluster.conflicts.push(item.conflict);
			} else {
				flush();
				cluster = { start: item.win.start, end: item.win.end, conflicts: [item.conflict] };
			}
		}
		flush();
	}
	return result;
});

function finalizeCard(cluster) {
	const first = cluster.conflicts[0];
	const meta = contested(first);
	const window = formatWindow(cluster.start, cluster.end);

	const parties = new Map();
	let roomCalendarUrl = null;
	for (const conflict of cluster.conflicts) {
		if (conflict.roomCalendarUrl && !roomCalendarUrl) {
			roomCalendarUrl = conflict.roomCalendarUrl;
		}
		for (const side of [conflict.a, conflict.b]) {
			if (side && !parties.has(sideKey(side))) {
				parties.set(sideKey(side), buildParty(side, first.level, window));
			}
		}
	}

	return {
		key: `${first.level}|${meta.key}|${cluster.start}-${cluster.end}`,
		level: first.level,
		contestedLabel: meta.label,
		date: first.date,
		window,
		parties: [...parties.values()],
		// Structured cohorts: the headline content for cohort cards, secondary info on room/lecturer.
		cohortGroups: mergeCohortGroups(cluster.conflicts.map((conflict) => conflict.cohorts)),
		// Present whenever an external room booking is involved (room or lecturer clash).
		roomCalendarUrl,
	};
}

const visibleCards = computed(() => cards.value.filter((card) => activeLevels.value.has(card.level)));

function compareCards(a, b) {
	if (a.date !== b.date) {
		return String(a.date || "").localeCompare(String(b.date || ""));
	}
	return String(a.window || "").localeCompare(String(b.window || ""));
}

const groups = computed(() => {
	const byKey = new Map();
	const add = (key, label, sortKey, level, card) => {
		if (!byKey.has(key)) {
			byKey.set(key, { key, label, sortKey, level, cards: [] });
		}
		byKey.get(key).cards.push(card);
	};

	for (const card of visibleCards.value) {
		if (groupBy.value === "level") {
			const meta = levelMeta[card.level];
			// Most critical level first (room → lecturer → cohort).
			add(card.level, meta ? meta.label : card.level, String(9 - (severity[card.level] || 0)), card.level, card);
		} else if (groupBy.value === "date") {
			add(card.date, formatConflictDate(card.date), card.date, null, card);
		} else {
			const sortKey = `${9 - (severity[card.level] || 0)}|${card.contestedLabel}`;
			add(`${card.level}|${card.contestedLabel}`, card.contestedLabel, sortKey, card.level, card);
		}
	}

	const result = [...byKey.values()].sort((a, b) =>
		String(a.sortKey).localeCompare(String(b.sortKey), "de", { numeric: true }),
	);
	for (const group of result) {
		group.cards.sort(compareCards);
	}
	return result;
});

function formatDateLong(date) {
	const [year, month, day] = (date || "").split("-").map(Number);
	if (!year || !month || !day) {
		return date;
	}
	const weekday = new Date(year, month - 1, day).toLocaleDateString("de-DE", { weekday: "short" });
	return `${weekday} ${formatConflictDate(date)}`;
}

return (_ctx, _cache) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!__props.appliedFilters)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [...(_cache[0] || (_cache[0] = [
          _createTextVNode(" Bitte Filter wählen und auf ", -1 /* CACHED */),
          _createElementVNode("strong", null, "Laden", -1 /* CACHED */),
          _createTextVNode(" klicken, um die Konflikte anzuzeigen. ", -1 /* CACHED */)
        ]))]))
      : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          (__props.loading)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [...(_cache[1] || (_cache[1] = [
                _createElementVNode("i", { class: "fa-solid fa-spinner fa-spin me-1" }, null, -1 /* CACHED */),
                _createTextVNode(" Prüfe Konflikte … ", -1 /* CACHED */)
              ]))]))
            : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                (__props.summary.total === 0)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_4, [...(_cache[2] || (_cache[2] = [
                      _createElementVNode("i", { class: "fa-solid fa-circle-check me-1" }, null, -1 /* CACHED */),
                      _createTextVNode(" Keine Konflikte für die gewählten Filter. ", -1 /* CACHED */)
                    ]))]))
                  : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                      _createElementVNode("div", _hoisted_5, [
                        _createElementVNode("span", _hoisted_6, _toDisplayString(__props.summary.total) + " " + _toDisplayString(__props.summary.total === 1 ? "Konflikt" : "Konflikte"), 1 /* TEXT */),
                        _createCommentVNode(" Per-level chips double as show/hide filters. "),
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(shownLevels.value, (level) => {
                          return (_openBlock(), _createElementBlock("button", {
                            key: level,
                            type: "button",
                            class: _normalizeClass(["btn btn-sm tt-chip", { 'tt-chip--off': !activeLevels.value.has(level) }]),
                            style: _normalizeStyle(activeLevels.value.has(level)
                ? { borderColor: levelMeta[level].color, color: levelMeta[level].color }
                : {}),
                            title: activeLevels.value.has(level) ? 'Ausblenden' : 'Einblenden',
                            onClick: $event => (toggleLevel(level))
                          }, [
                            _createElementVNode("i", {
                              class: _normalizeClass(['fa-solid', levelMeta[level].icon, 'me-1'])
                            }, null, 2 /* CLASS */),
                            _createTextVNode(" " + _toDisplayString(__props.summary[level]) + " " + _toDisplayString(levelMeta[level].label), 1 /* TEXT */)
                          ], 14 /* CLASS, STYLE, PROPS */, _hoisted_7))
                        }), 128 /* KEYED_FRAGMENT */)),
                        _createElementVNode("div", _hoisted_8, [
                          _cache[3] || (_cache[3] = _createElementVNode("span", { class: "align-self-center text-muted me-2 small" }, "Gruppieren nach", -1 /* CACHED */)),
                          (_openBlock(), _createElementBlock(_Fragment, null, _renderList(groupOptions, (option) => {
                            return _createElementVNode("button", {
                              key: option.value,
                              type: "button",
                              class: _normalizeClass(["btn", groupBy.value === option.value ? 'btn-secondary' : 'btn-outline-secondary']),
                              onClick: $event => (groupBy.value = option.value)
                            }, _toDisplayString(option.label), 11 /* TEXT, CLASS, PROPS */, _hoisted_9)
                          }), 64 /* STABLE_FRAGMENT */))
                        ])
                      ]),
                      (!visibleCards.value.length)
                        ? (_openBlock(), _createElementBlock("div", _hoisted_10, " Keine Konflikte für die aktuelle Filterung. Konflikttypen oben wieder einblenden. "))
                        : _createCommentVNode("v-if", true),
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(groups.value, (group) => {
                        return (_openBlock(), _createElementBlock("div", {
                          key: group.key,
                          class: "mb-4"
                        }, [
                          _createElementVNode("h6", _hoisted_11, [
                            (group.level)
                              ? (_openBlock(), _createElementBlock("i", {
                                  key: 0,
                                  class: _normalizeClass(['fa-solid', levelMeta[group.level].icon, 'me-2']),
                                  style: _normalizeStyle({ color: levelMeta[group.level].color })
                                }, null, 6 /* CLASS, STYLE */))
                              : _createCommentVNode("v-if", true),
                            _createTextVNode(" " + _toDisplayString(group.label) + " ", 1 /* TEXT */),
                            _createElementVNode("span", _hoisted_12, _toDisplayString(group.cards.length), 1 /* TEXT */)
                          ]),
                          _createElementVNode("div", _hoisted_13, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(group.cards, (card) => {
                              return (_openBlock(), _createElementBlock("div", {
                                key: card.key,
                                class: "card tt-card",
                                style: _normalizeStyle({ borderLeft: `4px solid ${levelMeta[card.level].color}` })
                              }, [
                                _createElementVNode("div", _hoisted_14, [
                                  _createElementVNode("div", {
                                    class: "fw-semibold",
                                    style: _normalizeStyle({ color: levelMeta[card.level].color })
                                  }, [
                                    _createElementVNode("i", {
                                      class: _normalizeClass(['fa-solid', levelMeta[card.level].icon, 'me-1'])
                                    }, null, 2 /* CLASS */),
                                    _createTextVNode(" " + _toDisplayString(card.level === "cohort" ? levelMeta.cohort.label : card.contestedLabel), 1 /* TEXT */)
                                  ], 4 /* STYLE */),
                                  _createElementVNode("div", _hoisted_15, [
                                    _cache[4] || (_cache[4] = _createElementVNode("i", { class: "fa-solid fa-calendar-day me-1" }, null, -1 /* CACHED */)),
                                    _createTextVNode(_toDisplayString(formatDateLong(card.date)) + " · " + _toDisplayString(card.window), 1 /* TEXT */)
                                  ]),
                                  _createElementVNode("ul", _hoisted_16, [
                                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(card.parties, (party) => {
                                      return (_openBlock(), _createElementBlock("li", {
                                        key: party.key
                                      }, [
                                        _cache[5] || (_cache[5] = _createElementVNode("span", { class: "tt-bullet me-1" }, "•", -1 /* CACHED */)),
                                        (party.url)
                                          ? (_openBlock(), _createElementBlock("a", {
                                              key: 0,
                                              href: party.url,
                                              target: "_blank",
                                              rel: "noopener"
                                            }, _toDisplayString(party.name), 9 /* TEXT, PROPS */, _hoisted_17))
                                          : (_openBlock(), _createElementBlock("span", _hoisted_18, _toDisplayString(party.name), 1 /* TEXT */)),
                                        (party.detail)
                                          ? (_openBlock(), _createElementBlock("span", _hoisted_19, " — " + _toDisplayString(party.detail), 1 /* TEXT */))
                                          : _createCommentVNode("v-if", true)
                                      ]))
                                    }), 128 /* KEYED_FRAGMENT */))
                                  ]),
                                  (card.cohortGroups.length)
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                                        (card.level !== 'cohort')
                                          ? (_openBlock(), _createElementBlock("div", _hoisted_21, [...(_cache[6] || (_cache[6] = [
                                              _createElementVNode("i", { class: "fa-solid fa-users me-1 text-muted" }, null, -1 /* CACHED */),
                                              _createTextVNode(" Betroffene Kohorten ", -1 /* CACHED */)
                                            ]))]))
                                          : _createCommentVNode("v-if", true),
                                        _createElementVNode("table", _hoisted_22, [
                                          _createElementVNode("tbody", null, [
                                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(card.cohortGroups, (cohort, gi) => {
                                              return (_openBlock(), _createElementBlock(_Fragment, { key: gi }, [
                                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList((cohort.entries.length ? cohort.entries : [emptyEntry]), (entry, ei) => {
                                                  return (_openBlock(), _createElementBlock("tr", { key: ei }, [
                                                    (ei === 0)
                                                      ? (_openBlock(), _createElementBlock("th", {
                                                          key: 0,
                                                          rowspan: cohort.entries.length || 1,
                                                          class: "tt-cohort-prefix"
                                                        }, _toDisplayString(cohort.prefix), 9 /* TEXT, PROPS */, _hoisted_23))
                                                      : _createCommentVNode("v-if", true),
                                                    _createElementVNode("td", _hoisted_24, _toDisplayString(entry.specialization), 1 /* TEXT */),
                                                    _createElementVNode("td", _hoisted_25, _toDisplayString(entry.semester), 1 /* TEXT */)
                                                  ]))
                                                }), 128 /* KEYED_FRAGMENT */))
                                              ], 64 /* STABLE_FRAGMENT */))
                                            }), 128 /* KEYED_FRAGMENT */))
                                          ])
                                        ])
                                      ]))
                                    : _createCommentVNode("v-if", true),
                                  _createElementVNode("div", _hoisted_26, [
                                    _createElementVNode("button", {
                                      type: "button",
                                      class: "btn btn-sm btn-link p-0 text-decoration-none",
                                      onClick: $event => (_ctx.$emit('jump-to-date', card.date))
                                    }, [...(_cache[7] || (_cache[7] = [
                                      _createElementVNode("i", { class: "fa-solid fa-calendar-day me-1" }, null, -1 /* CACHED */),
                                      _createTextVNode("Im Stundenplan ", -1 /* CACHED */)
                                    ]))], 8 /* PROPS */, _hoisted_27),
                                    (card.roomCalendarUrl)
                                      ? (_openBlock(), _createElementBlock("a", {
                                          key: 0,
                                          href: card.roomCalendarUrl,
                                          target: "_blank",
                                          rel: "noopener",
                                          class: "btn btn-sm btn-link p-0 text-decoration-none"
                                        }, [...(_cache[8] || (_cache[8] = [
                                          _createElementVNode("i", { class: "fa-solid fa-door-open me-1" }, null, -1 /* CACHED */),
                                          _createTextVNode("Im Raumkalender ", -1 /* CACHED */)
                                        ]))], 8 /* PROPS */, _hoisted_28))
                                      : _createCommentVNode("v-if", true)
                                  ])
                                ])
                              ], 4 /* STYLE */))
                            }), 128 /* KEYED_FRAGMENT */))
                          ])
                        ]))
                      }), 128 /* KEYED_FRAGMENT */))
                    ], 64 /* STABLE_FRAGMENT */))
              ], 64 /* STABLE_FRAGMENT */))
        ], 64 /* STABLE_FRAGMENT */))
  ]))
}
}

}